import { computed, DestroyRef, inject, Injectable, Injector, runInInjectionContext } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import {
  getDepartments,
  getExhibitionSettings,
  getOccupationalGroups,
  getProfessions,
  getTitles,
  isContinueAsGuest,
  selectIsGoogleMapsApiEnabled,
  selectIsLogoutProcessing,
  isUserLoggedIn as selectIsUserLoggedIn
} from '@app/app.reducer';
import {
  DynamicFormField,
  DynamicFormFields,
  FieldKey,
  FieldType,
  OptionKey
} from '@core/data-access/models/dynamic-form-field/dynamic-form-field.model';
import { NewsletterTranslationLabelKeys } from '@core/data-access/models/dynamic-form-field/newsletter-form-field-labels.model';
import { FormFactory } from '@core/data-access/models/form/form-mediator.model';
import { CheckboxFormField, FormFields } from '@core/data-access/models/form/form-store.model';
import { FormName } from '@core/data-access/models/form/form.model';
import { DynamicFieldOptionsTranslationFactory } from '@core/data-access/services/dynamic-form-field/dynamic-field-options-translation-factory.service';
import { DynamicFormFactory } from '@core/data-access/services/dynamic-form-field/dynamic-form-factory.service';
import { DynamicFormFieldBuilder } from '@core/data-access/services/dynamic-form-field/dynamic-form-field-builder.service';
import { selectIsReadOnlyBuyerFormFields } from '@core/data-access/store/helper.selectors';
import { selectSavedBuyerFormFields } from '@core/features/account/features/saved-ticket-holders/store/saved-ticket-holder.selectors';
import { BuyerInformationFormState } from '@core/features/buyer-information/models/buyer-information-form.model';
import {
  selectBuyerInformationFormFields,
  selectBuyerPerson,
  selectIsBuyerInformationFormPrefilled
} from '@core/features/buyer-information/store/buyer-information.selectors';
import { isFieldDisabled, isFieldHidden } from '@core/utils/dynamic-form-field/dynamic-form-field-helpers';
import { buyerInformationFormDynamicFormFieldParametersMapper } from '@core/utils/dynamic-form-field/dynamic-form-field-parameters-mapper';
import { disablePrefilledSavedBuyerFormFields } from '@core/utils/form-fields/form-field-helpers';
import { filterChangeFormFieldsPipe } from '@core/utils/form-fields/form-field-pipes';
import { select, Store } from '@ngrx/store';
import { ExhibitionSettingModel } from '@store/customization/customization.interfaces';
import { SelectOption } from '@store/exhibition/exhibition.interface';
import { getIsTranslationLoaded } from '@store/translation/translation.selectors';
import {
  filter as _filter,
  every,
  find,
  forEach,
  forIn,
  intersection,
  isEmpty,
  isNil,
  keys,
  maxBy,
  sortBy,
  values
} from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilKeyChanged,
  filter,
  first,
  map,
  merge,
  Observable,
  skip,
  switchMap,
  tap,
  withLatestFrom
} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class BuyerInformationFormFactory implements FormFactory {
  readonly #injector = inject(Injector);
  readonly #destroyRef = inject(DestroyRef);
  readonly #store = inject(Store);
  readonly #dynamicFormFieldBuilder = inject(DynamicFormFieldBuilder);
  readonly #dynamicFormFactory = inject(DynamicFormFactory);
  readonly #dynamicFieldOptionsTranslationFactory = inject(DynamicFieldOptionsTranslationFactory);

  readonly #initialState: BuyerInformationFormState = {
    viewModel: null,
    dynamicFormFields: [],
    form: {
      formName: FormName.BuyerInformation,
      formGroup: null
    },
    person: null
  };

  #isFormStateCreated = false;

  readonly #resetFormState$ = new BehaviorSubject(false);

  readonly #buyerPerson$ = this.#store.select(selectBuyerPerson);
  readonly #buyerInformationFormFields$ = this.#store.select(selectBuyerInformationFormFields);
  readonly #isReadOnlyBuyerFormFields$ = this.#store.select(selectIsReadOnlyBuyerFormFields);
  readonly #isUserLoggedIn$ = this.#store.select(selectIsUserLoggedIn);

  readonly state = toSignal(this.state$(), {
    initialValue: this.#initialState
  });

  resetFormState() {
    this.#resetFormState$.next(true);
  }

  private state$(): Observable<BuyerInformationFormState> {
    return merge(this.createFormState$(), this.updateFormState$(), this.resetFormState$()).pipe(
      takeUntilDestroyed(this.#destroyRef)
    );
  }

  private createFormState$() {
    return combineLatest({
      resetFormState: this.#resetFormState$,
      person: this.#buyerPerson$
    }).pipe(
      filter(({ resetFormState }) => {
        const isResetTriggeredOnInitialize = !resetFormState;
        const recreateFormAfterUserLogoutOrAnonymousVoucherRedeem = resetFormState && !this.#isFormStateCreated;

        return isResetTriggeredOnInitialize || recreateFormAfterUserLogoutOrAnonymousVoucherRedeem;
      }),
      withLatestFrom(
        this.#buyerInformationFormFields$,
        this.#isReadOnlyBuyerFormFields$,
        this.#isUserLoggedIn$,
        this.#store.select(selectSavedBuyerFormFields),
        this.#store.select(selectIsBuyerInformationFormPrefilled),
        this.#store.select(isContinueAsGuest)
      ),
      map(
        ([
          { person },
          formFields,
          isReadOnlyBuyerFormFields,
          isUserLoggedIn,
          savedBuyerFormFields,
          isBuyerInformationFormPrefilled,
          isContinueAsGuest
        ]) => ({
          person,
          formFields:
            isReadOnlyBuyerFormFields && isBuyerInformationFormPrefilled
              ? disablePrefilledSavedBuyerFormFields(formFields, savedBuyerFormFields)
              : formFields,
          isReadOnlyBuyerFormFields,
          isUserLoggedIn,
          isContinueAsGuest
        })
      ),
      switchMap(({ person, formFields, isReadOnlyBuyerFormFields, isUserLoggedIn, isContinueAsGuest }) =>
        this.createBuyerInformationDynamicFormFields$(formFields, isReadOnlyBuyerFormFields).pipe(
          tap((dynamicFormFields) =>
            this.#dynamicFieldOptionsTranslationFactory.createTranslatedDynamicFieldOptions(
              FormName.BuyerInformation,
              dynamicFormFields,
              true
            )
          ),
          map((dynamicFormFields) => {
            const formGroup = this.#dynamicFormFactory.createForm(dynamicFormFields, formFields);

            return {
              viewModel: this.setViewModel()(),
              dynamicFormFields: this.setCreateAccountDynamicFieldsFieldsVisibility(
                dynamicFormFields,
                formFields,
                formGroup,
                isUserLoggedIn,
                isContinueAsGuest
              ),
              form: {
                formName: FormName.BuyerInformation,
                formGroup
              },
              person
            };
          }),
          tap(() => (this.#isFormStateCreated = true))
        )
      )
    );
  }

  private updateFormState$() {
    return merge(
      this.updateFormStateOnFormFieldsChange$(),
      this.updateFormStateOnCreateAccountCheckboxChange$(),
      this.updateFormStateOnContinueAsGuestCheckboxChange$(),
      this.updateFormStateOnBuyerPersonIdChange$(),
      this.updateFormStateOnIsBuyerFormPrefilledChange$()
    );
  }

  private updateFormStateOnFormFieldsChange$() {
    return this.#buyerInformationFormFields$.pipe(
      filterChangeFormFieldsPipe(),
      filter((formFields) => this.#isFormStateCreated && !isEmpty(formFields)),
      map((formFields: FormFields) => {
        const { viewModel, dynamicFormFields, form, person } = this.state();

        return {
          viewModel,
          dynamicFormFields: this.#dynamicFormFactory.updateForm(dynamicFormFields, formFields, form.formGroup),
          form,
          person
        };
      })
    );
  }

  private updateFormStateOnCreateAccountCheckboxChange$() {
    return this.#buyerInformationFormFields$.pipe(
      filterChangeFormFieldsPipe(),
      filter(
        (formFields) => this.#isFormStateCreated && !isEmpty(intersection(keys(formFields), [FieldKey.CreateAccount]))
      ),
      withLatestFrom(this.#isUserLoggedIn$, this.#store.select(isContinueAsGuest)),
      map(([formFields, isUserLoggedIn, isContinueAsGuest]) => {
        const { viewModel, dynamicFormFields, form, person } = this.state();

        return {
          viewModel,
          dynamicFormFields: this.setCreateAccountDynamicFieldsFieldsVisibility(
            dynamicFormFields,
            formFields,
            form.formGroup,
            isUserLoggedIn,
            isContinueAsGuest
          ),
          form,
          person
        };
      })
    );
  }

  updateFormStateOnContinueAsGuestCheckboxChange$() {
    return this.#store.pipe(
      select(isContinueAsGuest),
      filter(() => this.#isFormStateCreated),
      withLatestFrom(this.#buyerInformationFormFields$, this.#isUserLoggedIn$),
      map(([isContinueAsGuest, formFields, isUserLoggedIn]) => {
        const { viewModel, dynamicFormFields, form, person } = this.state();

        return {
          viewModel,
          dynamicFormFields: this.setCreateAccountDynamicFieldsFieldsVisibility(
            dynamicFormFields,
            formFields,
            form.formGroup,
            isUserLoggedIn,
            isContinueAsGuest
          ),
          form,
          person
        };
      })
    );
  }

  private updateFormStateOnBuyerPersonIdChange$() {
    const skipFirstLoad = 1;

    return this.#buyerPerson$.pipe(
      skip(skipFirstLoad),
      filter((person) => this.#isFormStateCreated && !isNil(person)),
      distinctUntilKeyChanged('id'),
      map((person) => {
        const { viewModel, dynamicFormFields, form } = this.state();

        return {
          viewModel,
          dynamicFormFields,
          form,
          person
        };
      })
    );
  }

  private updateFormStateOnIsBuyerFormPrefilledChange$() {
    const skipFirstLoad = 1;

    return this.#store.pipe(
      select(selectIsBuyerInformationFormPrefilled),
      skip(skipFirstLoad),
      filter(() => this.#isFormStateCreated),
      withLatestFrom(this.#isReadOnlyBuyerFormFields$, this.#buyerInformationFormFields$, this.#isUserLoggedIn$),
      map(([isPrefilled, isReadOnlyBuyerFormFields, formFields, isUserLoggedIn]) => {
        const { dynamicFormFields, form, person } = this.state();

        const setPrefilledReadOnlyDynamicFormFields = isPrefilled && isReadOnlyBuyerFormFields;

        return {
          viewModel: this.setViewModel()(),
          dynamicFormFields: this.setCreateAccountDynamicFieldsFieldsVisibility(
            setPrefilledReadOnlyDynamicFormFields
              ? this.setReadOnlyDynamicFormFields(dynamicFormFields)
              : dynamicFormFields,
            formFields,
            form.formGroup,
            isUserLoggedIn,
            false
          ),
          form,
          person
        };
      })
    );
  }

  private resetFormState$() {
    return this.#resetFormState$.pipe(
      withLatestFrom(this.#store.select(selectIsLogoutProcessing)),
      filter(([resetFormState, isLogoutProcessing]) => resetFormState && !isLogoutProcessing),
      map(() => this.#initialState),
      tap(() => {
        this.#isFormStateCreated = false;

        this.#resetFormState$.next(false);
      })
    );
  }

  private createBuyerInformationDynamicFormFields$(formFields: FormFields, isReadOnlyBuyerInformationForm: boolean) {
    return combineLatest({
      isTranslationLoaded: this.#store.select(getIsTranslationLoaded),
      isGoogleMapsApiEnabled: this.#store.select(selectIsGoogleMapsApiEnabled),
      exhibitionSettings: this.#store.select(getExhibitionSettings),
      titles: this.#store.select(getTitles),
      professions: this.#store.select(getProfessions),
      departments: this.#store.select(getDepartments),
      occupationalGroups: this.#store.select(getOccupationalGroups)
    }).pipe(
      filter(({ isTranslationLoaded }) => isTranslationLoaded),
      first((combinedValues) => every(values(combinedValues), (value) => !isNil(value))),
      map(({ isGoogleMapsApiEnabled, exhibitionSettings, titles, professions, departments, occupationalGroups }) => {
        const buyerInformationDynamicFormFields = this.createBuyerInformationDynamicFormFields(
          formFields,
          isReadOnlyBuyerInformationForm,
          isGoogleMapsApiEnabled,
          exhibitionSettings,
          titles,
          professions,
          departments,
          occupationalGroups
        );

        const { order } = maxBy(buyerInformationDynamicFormFields, 'order')!;

        const fixedBuyerInformationFormFields = this.createFixedBuyerInformationDynamicFormFields(
          formFields,
          exhibitionSettings,
          order
        );

        const mergedBuyerInformationDynamicFormFields = [
          ...buyerInformationDynamicFormFields,
          ...fixedBuyerInformationFormFields
        ];

        return sortBy(mergedBuyerInformationDynamicFormFields, 'order');
      })
    );
  }

  private createBuyerInformationDynamicFormFields(
    formFields: FormFields,
    isReadOnlyBuyerInformationForm: boolean,
    isGoogleMapsApiEnabled: boolean,
    exhibitionSettings: ExhibitionSettingModel,
    titles: SelectOption[],
    professions: SelectOption[],
    departments: SelectOption[],
    occupationalGroups: SelectOption[]
  ) {
    const {
      buyerSettings: { fieldSettings },
      isTelephoneValidationDisabled
    } = exhibitionSettings;
    const isCountryFieldVisible = fieldSettings.Country.isVisible;

    const dynamicFormFields: DynamicFormFields = [];

    const createDynamicFormFieldMapper = this.#dynamicFormFieldBuilder.createDynamicFormFieldMapper();
    const createDynamicFormFieldParametersMapper = buyerInformationFormDynamicFormFieldParametersMapper(
      isReadOnlyBuyerInformationForm,
      isGoogleMapsApiEnabled,
      isTelephoneValidationDisabled,
      isCountryFieldVisible,
      titles,
      professions,
      departments,
      occupationalGroups
    );

    forIn(fieldSettings, (fieldSetting, fieldKey: FieldKey) => {
      const createDynamicFormFieldFunction = createDynamicFormFieldMapper[fieldKey];

      const createDynamicFormFieldParameters = {
        fieldSetting,
        ...createDynamicFormFieldParametersMapper[fieldKey]
      };

      const dynamicFormField: DynamicFormField = createDynamicFormFieldFunction(createDynamicFormFieldParameters);

      if (isNil(dynamicFormField)) {
        return;
      }

      dynamicFormField.isHidden = isFieldHidden(dynamicFormField, formFields);
      dynamicFormField.isDisabled = isFieldDisabled(dynamicFormField, formFields);

      dynamicFormFields.push(dynamicFormField);
    });

    return _filter(dynamicFormFields);
  }

  private createFixedBuyerInformationDynamicFormFields(
    formFields: FormFields,
    exhibitionSettings: ExhibitionSettingModel,
    order: number
  ) {
    const {
      buyerSettings: {
        showCreateAccountCheckbox,
        newsletterInputType,
        showFairCatalogCheckbox,
        fairCatalogDefaultValue,
        enableDifferentBillingAddress
      }
    } = exhibitionSettings;

    const passwordField = this.#dynamicFormFieldBuilder.createPasswordField({
      label: 'buyer-information.buyer-information-form.input.password.label',
      placeholder: 'buyer-information.buyer-information-form.input.password.placeholder',
      order: ++order
    });

    const verifyPasswordField = this.#dynamicFormFieldBuilder.createVerifyPasswordField({
      label: 'buyer-information.buyer-information-form.input.password-verify.label',
      placeholder: 'buyer-information.buyer-information-form.input.password-verify.placeholder',
      order: ++order
    });

    const createAccountField = this.#dynamicFormFieldBuilder.createCreateAccountField({
      label: '',
      checkboxOptionLabel: 'buyer-information.buyer-information-form.checkbox.create-account',
      order: ++order,
      showCreateAccountCheckbox
    });

    const createAccountButton = this.#dynamicFormFieldBuilder.createCreateAccountButton({
      label: 'buyer-information.buyer-information-form.button.create-account',
      order: ++order
    });

    const NewsletterLabelTranslationKey = NewsletterTranslationLabelKeys('buyer-information.buyer-information-form');

    const newsletterField = this.#dynamicFormFieldBuilder.createNewsletterField({
      checkboxHeading: 'buyer-information.buyer-information-form.checkbox.newsletter.heading',
      checkboxOptionLabel: NewsletterLabelTranslationKey.Checkbox,
      radioButtonHeading: 'buyer-information.buyer-information-form.radio.newsletter.heading',
      radioButtonOptionLabels: {
        optIn: NewsletterLabelTranslationKey.OptIn,
        optOut: NewsletterLabelTranslationKey.OptOut
      },
      order: ++order,
      newsletterInputType
    });

    const fairCatalogueField = this.#dynamicFormFieldBuilder.createFairCatalogueField({
      checkboxHeading: '',
      checkboxOptionLabel: 'buyer-information.buyer-information-form.checkbox.fair-catalogue.label',
      order: ++order,
      showFairCatalogCheckbox,
      fairCatalogDefaultValue
    });

    const differentBillingAddressField = this.#dynamicFormFieldBuilder.createDifferentBillingAddressField({
      checkboxHeading: '',
      checkboxOptionLabel: 'buyer-information.buyer-information-form.checkbox.different-billing-address.label',
      order: ++order,
      enableDifferentBillingAddress
    });

    const createdDynamicFormFields = _filter([
      passwordField,
      verifyPasswordField,
      createAccountField,
      newsletterField,
      createAccountButton,
      fairCatalogueField,
      differentBillingAddressField
    ]);

    forEach(
      createdDynamicFormFields,
      (createdDynamicFormField) =>
        (createdDynamicFormField.isHidden = isFieldHidden(createdDynamicFormField, formFields))
    );

    return createdDynamicFormFields;
  }

  private setViewModel() {
    return runInInjectionContext(this.#injector, () => {
      const isUserLoggedIn = this.#store.selectSignal(selectIsUserLoggedIn);
      const isReadonlyBuyerFormFields = toSignal(this.#isReadOnlyBuyerFormFields$);

      return computed(() => ({
        isUserLoggedIn: isUserLoggedIn(),
        isReadOnlyBuyerFormFields: isReadonlyBuyerFormFields()
      }));
    });
  }

  private setReadOnlyDynamicFormFields(dynamicFormFields: DynamicFormFields) {
    const zipCodeDynamicFormField = find(dynamicFormFields, ({ key }) => key === FieldKey.ZipCode);
    const addressDynamicFormField = find(dynamicFormFields, ({ key }) => key === FieldKey.Address);

    if (!isNil(zipCodeDynamicFormField)) {
      zipCodeDynamicFormField.type = FieldType.TextInput;
    }

    if (!isNil(addressDynamicFormField)) {
      addressDynamicFormField.isHidden = true;
    }

    return dynamicFormFields;
  }

  private setCreateAccountDynamicFieldsFieldsVisibility(
    dynamicFormFields: DynamicFormFields,
    formFields: FormFields,
    formGroup: FormGroup,
    isUserLoggedIn: boolean,
    isContinueAsGuest: boolean
  ) {
    const createAccountDynamicFormField = find(dynamicFormFields, ({ key }) => key === FieldKey.CreateAccount);

    if (isNil(createAccountDynamicFormField)) {
      return dynamicFormFields;
    }

    const createAccountFormControl = formGroup?.get(FieldKey.CreateAccount);

    createAccountDynamicFormField.isHidden = isUserLoggedIn || isContinueAsGuest;
    createAccountDynamicFormField.isDisabled = isUserLoggedIn || isContinueAsGuest;
    createAccountDynamicFormField.isDisabled ? createAccountFormControl?.disable() : createAccountFormControl?.enable();

    const createAccountCheckboxField = formFields[FieldKey.CreateAccount] as CheckboxFormField;
    const createAccountCheckboxFieldValue = createAccountCheckboxField?.value[OptionKey.CreateAccount].value;

    forEach([FieldKey.Password, FieldKey.VerifyPassword, FieldKey.CreateAccountButton], (fieldKey) => {
      const dynamicFormField = find(dynamicFormFields, ({ key }) => key === fieldKey);

      if (isNil(dynamicFormField)) {
        return;
      }

      const fieldFormControl = formGroup?.get(fieldKey);

      const hideAndDisableField = isUserLoggedIn || isContinueAsGuest || !createAccountCheckboxFieldValue;

      dynamicFormField.isHidden = hideAndDisableField;
      dynamicFormField.isDisabled = hideAndDisableField;
      dynamicFormField.isDisabled ? fieldFormControl?.disable() : fieldFormControl?.enable();
    });

    return dynamicFormFields;
  }
}
